import { getInstance } from '../../../common/api/spidertracks-sdk';
import { StandardThunk } from '../../../store';
import {
  AircraftTypeEnum,
  FlightDetail,
  UIFlightLogs,
  UILegacyEvent
} from '../../../types/flyingPage';
import {
  setLatestFlightDetails,
  setLoading,
  setUIFlightLogs,
  setUILegacyEvents,
  setFlightlogsIntialLoad
} from './flyingPage';
import { PrivateAircraftData } from '../../../common/api/spidertracks-sdk/types/AircraftData';
import { PrivateTrackData } from '../../../common/api/spidertracks-sdk/types/TrackData';
import { getIsActive } from '../tracks';
import { getTrackStatus } from '../../../components/Flying/AircraftList/utils/aircraftlistUtils';
import {
  getAircraftTypesMap,
  getFlightlogsInitialLoad,
  getSerialNumberBootCountCombinations,
  getLatestPointForSerialAndBoot
} from '../../selectors/flyingPage';
import { FlightLogsEvent } from '../../../common/api/spidertracks-sdk/types/FlightLogs';
import {
  getEndEventForUI,
  getStartEventForUI,
  getTOLEventsForUI
} from '../../flightlogsMappers/flightlogsMapper';
import { getFilteredAndPairedEvents } from '../../flightlogsMappers/eventFilter';
import {
  getSpeedUpOrDownEventsForUI,
  getLegacyStartEventForUI,
  getLegacyEndEventForUI
} from '../../legacyEventsMapper';
import { getSerialBootCountKey } from "../../flightlogsMappers/util";

function mapAircraftTypeToEnum(type: string | undefined): AircraftTypeEnum {
  switch (type) {
    case 'OTHER':
      return AircraftTypeEnum.OTHER;
    case 'FIXED_WING':
      return AircraftTypeEnum.FIXED_WING;
    case 'ROTARY_WING':
      return AircraftTypeEnum.ROTARY;
    default:
      return AircraftTypeEnum.UNDEFINED;
  }
}

export const fetchLatestFlightDetails = (organisationSlug: string): StandardThunk<void> => async (
  dispatch,
  getState
) => {
  const sdk = getInstance();
  const aircrafts = (await sdk.getAircraft(organisationSlug)) as PrivateAircraftData[];
  // TODO: optimize points fetched count later.
  const latestTrack = (await sdk.getLatestTracks(organisationSlug, 10000)) as PrivateTrackData[];
  const latestFlightDetails: FlightDetail[] = latestTrack
    .map(track => {
      const aircraft = aircrafts.find(aircraft => aircraft.id === track.aircraft.id);
      if (aircraft) {
        return {
          aircraftId: aircraft.id,
          aircraft: {
            registration: aircraft.registration,
            type: mapAircraftTypeToEnum(aircraft.type),
            isActive: getIsActive(track.points),
            trackStatus: getTrackStatus(track.state, getIsActive(track.points)),
            aircraftIcon: aircraft.aircraft_icon,
            aircraftIconColor: aircraft.aircraft_icon_colour
          },
          activeDeviceConnection: track.aircraft.activeDeviceConnection ?? undefined,
          track: track
        };
      }
    })
    .filter(flightDetail => flightDetail !== undefined) as FlightDetail[];

  dispatch(setLatestFlightDetails(latestFlightDetails));
  dispatch(setLoading(false));

  let uiLegacyEvents: {
    [key: string]: {
      startEvent: UILegacyEvent;
      endEvent: UILegacyEvent;
      speedUpOrDownEvents: UILegacyEvent[];
    };
  } = {};

  latestFlightDetails.forEach(flightDetail => {
    uiLegacyEvents[flightDetail.aircraftId] = {
      startEvent: getLegacyStartEventForUI(flightDetail.track.points[0]),
      speedUpOrDownEvents: getSpeedUpOrDownEventsForUI(flightDetail.track.points),
      endEvent: getLegacyEndEventForUI(
        flightDetail.track.points[0],
        flightDetail.track.points[flightDetail.track.points.length - 1]
      )
    };
  });

  dispatch(setUILegacyEvents({ UILegacyEvents: uiLegacyEvents }));
};

export const fetchFlightLogs = (): StandardThunk<void> => async (dispatch, getState) => {
  try {
    const flightLogsService = getInstance().getFlightLogsService();
    const bcSerialCombinations = getSerialNumberBootCountCombinations(getState());

    if (bcSerialCombinations.length === 0) {
      return;
    }

    const flightlogsFromAPI = await flightLogsService.getFlightLogsForDeviceTrack(
      bcSerialCombinations
    );
    const uiFlightLogs: {
      [key: string]: {
        startEvent: UIFlightLogs | undefined;
        endEvent: UIFlightLogs | undefined;
        takeoffAndLandingEvents: UIFlightLogs[];
      };
    } = {};

    flightlogsFromAPI.forEach(flightlog => {
      const serialBcId = getSerialBootCountKey(flightlog.serialNumber, flightlog.bootCount);
      const events: FlightLogsEvent[] = [
        ...(flightlog.flightEvents.length > 0 ? flightlog.flightEvents : []),
        ...(flightlog.engineEvents.length > 0 ? flightlog.engineEvents : []),
        ...(flightlog.powerEvents.length > 0 ? flightlog.powerEvents : [])
      ];

      const aircraftIdTypeMap = getAircraftTypesMap(getState());
      const aircraftType = aircraftIdTypeMap.get(flightlog.aircraftId);

      // We don't know how to process flight logs for these aircraft types
      if (
        !aircraftType ||
        aircraftType === AircraftTypeEnum.UNDEFINED ||
        aircraftType === AircraftTypeEnum.OTHER
      ) {
        return;
      }

      const filteredEvents = getFilteredAndPairedEvents(events);
      const latestPoint = getLatestPointForSerialAndBoot(
        flightlog.serialNumber,
        flightlog.bootCount
      )(getState());

      uiFlightLogs[serialBcId] = {
        startEvent: getStartEventForUI(filteredEvents, aircraftType),
        endEvent: getEndEventForUI(filteredEvents, aircraftType),
        takeoffAndLandingEvents: getTOLEventsForUI(filteredEvents, latestPoint)
      };
    });

    dispatch(setUIFlightLogs({ UIFlightLogs: uiFlightLogs }));
  } catch (error) {
    console.error('Error fetching flight logs', error);
  } finally {
    const flightlogsIntialLoad = getFlightlogsInitialLoad(getState());
    if (!flightlogsIntialLoad) dispatch(setFlightlogsIntialLoad(true));
  }
};
